import _ from 'underscore';
import defaultHeaderTemplate from './template/header-template.pug';
import cellTemplate from './template/cell-template.pug';

/**
 * @const {Object} COLUMN_TYPES the different types supported by ProjectionGridBaseColumn
 * @property {string} NUMBER value: 'number'
 *    Will add text-right class to column
 * @property {string} DATE value: 'date'
 *    Will add text-right class to column
 */
export const COLUMN_TYPES = {
  NUMBER: 'number',
  DATE: 'date',
};

/**
 * @const {Object} COLUMN_FORMATS The different formats supported by ProjectionGridBaseColumn
 * @property {string} PERCENTAGE value: 'percentage'
 *    Convert a float to a percentage. E.g.: .07 => 7%, .15 => 15%
 * @property {string} PERCENTAGE_VALUE value: 'percentage-value'
 *    Formats a float as percentage. E.g.: 42.07 => 42.07%, 0.15 => 0.15%
 * @property {string} INTEGER value: 'integer',
 *    Formats value as integer.
 * @property {string} NATURAL: value: 'natural-integer'
 *    A positive non-zero integer: 1,2,3..Number.MAX_VALUE
 * @property {string} DECIMAL: value: 'decimal'
 *    A number with two decimal points.
 */
export const COLUMN_FORMATS = {
  PERCENTAGE: 'percentage',
  PERCENTAGE_VALUE: 'percentage-value',
  INTEGER: 'integer',
  NATURAL: 'natural-integer',
  DECIMAL: 'decimal',
  THOUSANDTH: 'thousandth',
};

const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
};

const COLUMN_PROPERTIES = [
  // ColumnConfig properties
  'name',
  'title',
  'html',
  'property',
  'group',
  'template',
  'sortable',
  'sortDirection',
  'colClasses',
  'headClasses',
  'bodyClasses',
  'footClasses',
  'colAttributes',
  'headAttributes',
  'bodyAttributes',
  'footAttributes',

  // Plugin properties
  'editable',
  'isResizable',
  'twoRowsProperties',
  'columnConversion',
];

/**
 * Normalizes the input classes into the expected object format. If the input is a string, uses
 * delimiters ',' and ' '.
 * e.g. result
 * bodyClasses: {
 *   increase: value => value > 0,
 *   decrease: value => value < 0,
 * }
 *
 * @param {Array} classes input classes
 * @returns {Object} Object where keys represent the class name
 */
function normalizeClasses(classes) {
  if (_.isString(classes)) {
    return normalizeClasses(classes.split(/[, ]/));
  }

  if (_.isArray(classes)) {
    const names = _.chain(classes).compact().uniq().value();
    return _.object(names, _.times(names.length, () => true));
  }

  if (_.isObject(classes)) {
    return classes;
  }

  throw new Error('Invalid classes');
}

/**
 * Merges and normalises multiple classes objects, also compacts and removes duplicates.
 * If an input is a string, uses delimiters ',' and ' '.
 *
 * @param {any} args any number of classes input
 * @returns {Object} the merged result.
 */
export function mergeClasses(...args) {
  return _.extend({}, ..._.map(args, normalizeClasses));
}

/**
 * Returns the alignment class related to this column, may be empty.
 *
 * @param {any} column the first set of items
 * @returns {Array} the combined list.
 */
export function alignClasses(column) {
  const { type } = column.config;

  if (type) {
    const alignClass = _.contains([COLUMN_TYPES.NUMBER, COLUMN_TYPES.DATE], type) ? 'text-right' : 'text-left';

    return [alignClass];
  }

  return [];
}

/**
 * Constructs an array of grid column configuration objects for consumption by projection grid
 * [ColumnConfig]{@link https://github.com/Microsoft/projection-grid/blob/master/js/vnext/projection/columns.js}.
 *
 * @param {Array} columns The list of column classes to be constructed.
 * @param {any} args Arguments to be passed into the constructor.
 * @returns {Array} The list of column configs used to configure a projection grid.
 */
export function mapColumnsConfig(columns, ...args) {
  return _.chain(columns)
    .compact()
    .map(Column => (new Column(...args).toJSON()))
    .value();
}


function getFormatter(config) {
  const {
    format,
    i18n,
    placeholder,
  } = config;
  let formatter = _.identity;

  if (!format) {
    return formatter;
  }

  switch (format) {
    case COLUMN_FORMATS.PERCENTAGE:
      formatter = _.compose(i18n.percentFormatter(), Number);
      break;
    case COLUMN_FORMATS.PERCENTAGE_VALUE:
      formatter = _.compose(i18n.percentToFixedFormatter({
        digits: 2,
      }), value => value / 100, Number);
      break;
    case COLUMN_FORMATS.INTEGER:
      formatter = _.compose(i18n.decimalToIntegerFormatter(), Number);
      break;
    case COLUMN_FORMATS.NATURAL:
      formatter = (() => {
        const fmt = _.compose(i18n.decimalToIntegerFormatter(), Number);
        return value => (_.isFinite(value) && value >= 1 ? fmt(value) : placeholder);
      })();
      break;
    case COLUMN_FORMATS.DECIMAL:
      formatter = _.compose(i18n.decimalToFixedFormatter(), Number);
      break;
    case COLUMN_FORMATS.THOUSANDTH:
      formatter = _.compose(i18n.decimalToFixedFormatter(), value => value / 1000, Number);
      break;
    default:
      break;
  }

  return value => (_.isNull(value)
    || _.isUndefined(value)
    || (_.has(_.invert(COLUMN_FORMATS), format) && value === '') ?
    placeholder :
    formatter(value));
}

/**
 * Provides a base column configuration for projection grid.
 * This column provides helpful functions to style the column and provides a clean object-oriented
 * design to create shareable column code.
 * See {@link https://microsoft.github.io/projection-grid/} for description of the fields you can set.
 * All getter properties are from [ColumnConfig]{@link https://github.com/Microsoft/projection-grid/blob/master/js/vnext/projection/columns.js}
 * as well as some plugin properties (editable).
 * @param {Object} config Configurations for the column
 * @param {string} config.name
 *    Name of the column.
 * @param {string} config.title
 *    The localized column title. The `name` will be used if `title` is omitted.
 * @param {string} config.property
 *    The data property of the column. The base column expects a string, to use other types for
 *    properties override the property getter in a subclass.
 *    A direction will be added based on the {@link config.type} passed in.
 * @param {string} config.group
 *    The columns will be separated by several group based on this property.
 * @param {(boolean|number|OrderByKey)} config.sortable
 *    The ordering configuration. The base column expects a string, to use other types for
 *    sortable override the property getter in a subclass.
 * @param {string} config.sortDirection
 *    The sort direction. Either 'desc' or 'asc'
 * @param {ClassesConfig} config.colClasses
 *    The classes for the `COL` element in `COLGROUP`.
 * @param {ClassesConfig} config.headClasses
 *    The classes for the `TH` element in `THEAD`.
 * @param {ClassesConfig} config.bodyClasses
 *    The classes for the `TD` elements in `TBODY`.
 * @param {ClassesConfig} config.footClasses
 *    The classes for the `TD` elements in `TFOOT`.
 * @param {ClassesConfig} config.colAttributes
 *    The attributes for the `COL` element in `COLGROUP`.
 * @param {AttributesConfig} config.headAttributes
 *    The attributes for the `TH` elements in `THEAD`.
 * @param {AttributesConfig} config.bodyAttributes
 *    The attributes for the `TD` elements in `TBODY`.
 * @param {AttributesConfig} config.footAttributes
 *    The attributes for the `TD` elements in `TFOOT`.
 * @param {Object} config.parentView
 *    The parent view of the column.
 * @param {Object} config.context
 *    The context for the column, this should provide contextual information such as current
 *    permissions, scope, etc.
 * @param {Object} config.helpId
 *    A popup Id that will be rendered in the default header html.
 * @param {Object} config.i18n
 *    A i18n class, used for the formatter and to get the title/tooltip string if provided.
 * @param {Object} config.tooltip
 *    A tooltip added to the headAttributes, will use i18n to get string if provided.
 * @param {Object} config.format
 *    The {@link COLUMN_FORMATS} of the column.
 * @param {Object} config.type
 *    The {@link COLUMN_TYPES} of the column.
 * @param {String} config.placeholder
 *    Default '-'. The placeholder used by the format if no property is found for the row.
 * @param {Function} config.headerTemplate
 *    The template to be used for the header html. Will be rendered with the title and helpId.
 * @param {Object} config.html
 *    The html for header.
 * @param {Object} config.editorConfig
 *    The editorConfig for the 'editable' property.
 * @param {Function} config.enableEdit, whether enable edit for specific cell, false to disable
 * @param {boolean} config.isResizable
 *    whether enable column resizing, used by projection-grid-column-resize plugin
 * @param {Object} config.twoRowsProperties
 *    whether enable two rows column, used by projection-grid-two-rows plugin
 *
 * @class ProjectionGridBaseColumn
 */
export class ProjectionGridBaseColumn {
  constructor({
    // ColumnConfig values
    name,
    title,
    property,
    group,
    sortable = false,
    sortDirection,
    colClasses = [],
    headClasses = [],
    bodyClasses = [],
    footClasses = [],
    colAttributes = {},
    headAttributes = {},
    bodyAttributes = {},
    footAttributes = {},
    // Custom values
    parentView = null,
    context = null,
    helpId = null,
    customHelpContent = null,
    i18n = null,
    tooltip = null,
    format = null,
    type = null,
    placeholder = '-',
    headerTemplate = defaultHeaderTemplate,
    html,
    editorConfig = null,
    enableEdit = null,
    helpAriaLabel = '',
    isResizable,
    twoRowsProperties = null,
    columnConversion = null,
  }) {
    // Validate constructor arguments
    if (!name || !property || !title) {
      throw new Error('ProjectionGridBaseColumn requires a name, property and title');
    } else if (name.indexOf('\\') !== -1 || name.indexOf('/') !== -1) {
      throw new Error('name property shouldn\'t contain a slash character');
    }

    if (format && !i18n) {
      throw new Error('i18n is required to use the format option');
    }

    // Set config
    this.config = {
      name,
      title,
      property,
      group,
      parentView,
      context,
      sortable,
      sortDirection,
      helpId,
      customHelpContent,
      i18n,
      tooltip,
      format,
      type,
      placeholder,
      headerTemplate,
      html,
      colClasses,
      headClasses,
      bodyClasses,
      footClasses,
      colAttributes,
      headAttributes,
      bodyAttributes,
      footAttributes,
      editorConfig,
      enableEdit,
      helpAriaLabel,
      isResizable,
      twoRowsProperties,
      columnConversion,
    };

    this.cached = {};
  }

  get name() {
    return this.config.name;
  }

  get title() {
    return this.config.i18n ? this.config.i18n.getString(this.config.title) : this.config.title;
  }

  get property() {
    return {
      get: value => this.pickProperty(value),
      key: this.config.property,
    };
  }

  get group() {
    return this.config.group;
  }

  /**
   * Default picks the property from config.property, uses '/' to access sub key values.
   * @private
   * @param {any} model - The model returned from the server
   * @returns {any} - A subset of properties relevant to the column.
   */
  pickProperty(model) {
    return _.reduce(this.config.property.split('/'), (memo, key) => _.result(memo, key), model);
  }

  get parentView() {
    return this.config.parentView;
  }

  get context() {
    return this.config.context;
  }

  get sortable() {
    const { sortable, type, sortDirection } = this.config;

    if (!_.isString(sortable)) {
      return sortable || false;
    }

    let direction;
    if (_.isUndefined(sortDirection)) {
      direction = _.contains([COLUMN_TYPES.NUMBER, COLUMN_TYPES.DATE], type) ? -1 : 1;
    } else {
      direction = _.isEqual(SORT_DIRECTION.DESC, sortDirection) ? -1 : 1;
    }

    return {
      direction,
      key: sortable,
    };
  }

  get isResizable() {
    return this.config.isResizable;
  }

  get helpId() {
    return this.config.helpId;
  }

  get twoRowsProperties() {
    return this.config.twoRowsProperties;
  }

  get colClasses() {
    return mergeClasses(this.config.colClasses);
  }

  get headClasses() {
    return mergeClasses(alignClasses(this), this.config.headClasses);
  }

  get bodyClasses() {
    return mergeClasses(alignClasses(this), this.config.bodyClasses);
  }

  get footClasses() {
    return mergeClasses(alignClasses(this), this.config.footClasses);
  }

  get colAttributes() {
    return this.config.colAttributes;
  }

  get headAttributes() {
    const { headAttributes, tooltip, i18n } = this.config;

    if (tooltip) {
      headAttributes.title = i18n ? i18n.getString(tooltip) : tooltip;
    }

    return headAttributes;
  }

  get bodyAttributes() {
    return this.config.bodyAttributes;
  }

  get footAttributes() {
    return this.config.footAttributes;
  }

  get html() {
    const {
      headerTemplate,
      html,
      helpId,
      helpAriaLabel,
      customHelpContent,
    } = this.config;

    return html || headerTemplate({
      title: this.title,
      helpId,
      customHelpContent,
      ariaLabel: helpAriaLabel,
      sortable: !!this.sortable,
    });
  }

  get editable() {
    const { editorConfig, enableEdit } = this.config;
    const editable = {};

    if (editorConfig) {
      editable.editorConfig = editorConfig;
    } else if (_.isFunction(this.onPrompt)) {
      editable.prompt = value => this.onPrompt(value);
    }

    if (enableEdit && _.isFunction(enableEdit)) {
      editable.enableEdit = enableEdit;
    }

    return _.isEmpty(editable) ? false : editable;
  }

  get formatter() {
    if (!this.cached.formatter) {
      this.cached.formatter = getFormatter(this.config);
    }
    return this.cached.formatter;
  }

  get columnConversion() {
    return this.config.columnConversion;
  }

  template({ value }) {
    return cellTemplate({ value: this.formatter(value) });
  }

  /**
   * Converts class to plain (JSON) object.
   * The projection grid expects a JSON object for the column. All class functions will be lost
   * unless the class is JSONified.
   * This function binds all property getters or function to itself.
   *
   * @returns {Object} this - a JSONified version of this
   */
  toJSON() {
    return _.chain(this)
      .pick(COLUMN_PROPERTIES)
      .mapObject(value => (_.isFunction(value) ? value.bind(this) : value))
      .value();
  }
}
